import { TAG_FQN_PREFIXES } from '@/shared/lib/constants';
import type { BasicTagInfo } from '@/shared/lib/tag';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import type { TagLabel, TagLabelType, TagSource, TagState } from '@/shared/types/openmetadata';

import type { TagItem } from '@/entities/tags';

/**
 * @deprecated Use BasicTagInfo from '@/shared/lib/tag' instead
 */
export type FilteredTag = BasicTagInfo;

export type TagFilterOptions = {
  excludeTier?: boolean;
  excludeCertification?: boolean;
  requireConfirmed?: boolean;
  includeOnlySource?: TagSource;
};

type TagInput = {
  tagFQN: string;
  name: string;
  displayName?: string;
  description?: string;
  style?: {
    color?: string;
    iconURL?: string;
  };
  source: string;
  state: string;
  labelType: string;
};

export const filterEntityLabels = (
  tags:
    | (
        | TagLabel
        | {
            tagFQN: string;
            name?: string;
            source?: string;
            state?: string;
            labelType?: string;
            displayName?: string;
            description?: string;
            style?: { color?: string; iconURL?: string };
          }
      )[]
    | undefined,
  options: TagFilterOptions = {}
): TagInput[] => {
  if (!tags) {
    return [];
  }

  const { excludeTier = false, excludeCertification = false, requireConfirmed = false, includeOnlySource } = options;

  return tags
    .filter((tag): tag is TagInput => !!tag.name && !!tag.tagFQN && !!tag.source && !!tag.state && !!tag.labelType)
    .filter((tag) => {
      if (excludeTier && tag.tagFQN.startsWith(TAG_FQN_PREFIXES.TIER)) return false;
      if (excludeCertification && tag.tagFQN.startsWith(TAG_FQN_PREFIXES.CERTIFICATION)) return false;
      if (includeOnlySource && tag.source !== includeOnlySource) return false;
      if (requireConfirmed && tag.state !== 'Confirmed') return false;

      return true;
    });
};

export const filterAndMapTags = (
  tags:
    | (
        | TagLabel
        | { tagFQN: string; name?: string; source?: string; state?: string; labelType?: string; displayName?: string }
      )[]
    | undefined
): BasicTagInfo[] => {
  const filtered = filterEntityLabels(tags, {
    includeOnlySource: 'Classification',
    excludeTier: true,
    requireConfirmed: true,
  });

  const basicInfo = mapTagsToBasicInfo(filtered);

  return basicInfo.filter((tag, index, self) => index === self.findIndex((t) => t.tagFQN === tag.tagFQN));
};

export const convertToTagItem = (tag: TagInput): TagItem => ({
  tagFQN: tag.tagFQN,
  name: tag.name,
  displayName: tag.displayName,
  description: tag.description,
  style: tag.style,
  source: tag.source as TagSource,
  labelType: tag.labelType as TagLabelType,
  state: tag.state as TagState,
});

export const convertToTagItems = (tags: TagInput[]): TagItem[] => tags.map(convertToTagItem);

/**
 * @deprecated Use filterEntityLabels instead
 */
export const filterTags = filterEntityLabels;
